import React, { useState, useRef, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { getRecommendationsByProjectId, ProjectRecommendation } from "../../../services/grantsApi";
import { formatDateRange } from "../../../utils/date-formatters";
import {
  ACTION_BADGES,
  RECOMMENDATION_STATUS_LABELS,
  RECOMMENDATION_STATUS_COLORS,
} from "../../../constants/grants-labels";

/**
 * Props for RelatedRecommendationsSection component
 * @see docs/specifications/2026-01-30-recommendation-details-project-context.md
 * @see docs/specifications/2026-01-30-related-recommendations-count-preload.md
 */
interface RelatedRecommendationsSectionProps {
  projectId: string;
  currentRecommendationId: string;
  /** Preloaded count from recommendation details API (displayed before expanding) */
  initialCount?: number;
}

/**
 * Returns Tailwind CSS color class for confidence score visualization.
 */
const getConfidenceColor = (confidence: number): string => {
  if (confidence >= 0.8) return "text-green-600";
  if (confidence >= 0.6) return "text-yellow-600";
  return "text-red-600";
};

/**
 * Chevron icon for expandable section
 */
const ChevronIcon: React.FC<{ expanded: boolean }> = ({ expanded }) => (
  <svg
    className={`h-5 w-5 transition-transform duration-200 ${expanded ? "rotate-180" : ""}`}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    aria-hidden="true"
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

/**
 * Skeleton loader for recommendations list
 */
const RecommendationsSkeleton: React.FC = () => (
  <div className="space-y-2" aria-label="Ładowanie rekomendacji...">
    {[1, 2].map((i) => (
      <div key={i} className="animate-pulse flex items-center gap-4 p-2 border border-neutral-200 rounded-md">
        <div className="flex-1 space-y-2">
          <div className="h-4 bg-neutral-200 rounded w-2/3"></div>
          <div className="h-3 bg-neutral-200 rounded w-1/3"></div>
        </div>
        <div className="h-6 bg-neutral-200 rounded w-16"></div>
      </div>
    ))}
  </div>
);

/**
 * Error alert with retry button (compact version)
 */
const ErrorAlert: React.FC<{ message: string; onRetry: () => void }> = ({ message, onRetry }) => (
  <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm">
    <div className="flex items-center justify-between">
      <span className="text-red-700">{message}</span>
      <button
        onClick={onRetry}
        className="px-2 py-1 text-xs font-medium text-red-700 bg-white border border-red-300 rounded hover:bg-red-50"
      >
        Odśwież
      </button>
    </div>
  </div>
);

/**
 * Single recommendation row in the list
 */
const RecommendationRow: React.FC<{ recommendation: ProjectRecommendation }> = ({ recommendation }) => {
  const actionBadge = ACTION_BADGES[recommendation.suggestedAction] || {
    label: recommendation.suggestedAction,
    className: "bg-neutral-100 text-neutral-800",
  };
  const statusLabel = RECOMMENDATION_STATUS_LABELS[recommendation.status] || recommendation.status;
  const statusColor = RECOMMENDATION_STATUS_COLORS[recommendation.status] || "bg-neutral-200 text-neutral-800";

  const dates = recommendation.extractedDataSnapshot;
  const dateRange = formatDateRange(dates?.applicationStartDate, dates?.applicationEndDate);

  return (
    <Link
      to={`/grants/recommendations/${recommendation.id}`}
      className="block p-2 rounded-md border border-neutral-200 hover:bg-neutral-50 transition-colors focus-visible:ring-2 focus-visible:ring-primary-500"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="text-sm text-neutral-900 truncate">
            {recommendation.article?.title || "Bez tytułu"}
          </div>
          <div className="mt-0.5 flex items-center gap-3 text-xs text-neutral-500">
            <span className={`font-medium ${getConfidenceColor(recommendation.confidence)}`}>
              {(recommendation.confidence * 100).toFixed(0)}%
            </span>
            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs ${actionBadge.className}`}>
              {actionBadge.label}
            </span>
            {dateRange !== "—" && <span>{dateRange}</span>}
          </div>
        </div>
        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${statusColor}`}>
          {statusLabel}
        </span>
      </div>
    </Link>
  );
};

/**
 * Collapsible section displaying related recommendations in a project.
 * Lazy loads data only when expanded.
 *
 * @see docs/specifications/2026-01-30-recommendation-details-project-context.md
 */
const RelatedRecommendationsSection: React.FC<RelatedRecommendationsSectionProps> = ({
  projectId,
  currentRecommendationId,
  initialCount,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);

  // Lazy loading - only fetch when expanded
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["project-recommendations", projectId],
    queryFn: () => getRecommendationsByProjectId(projectId, 20),
    enabled: !!projectId && isExpanded, // Only fetch when expanded
    staleTime: 60000, // 1 minute
  });

  // Filter out current recommendation
  const recommendations = (data?.data || []).filter(
    (rec) => rec.id !== currentRecommendationId
  );
  const totalCount = (data?.pagination?.totalCount || 0) - 1; // Exclude current from count

  // Focus management after toggle
  const pendingFocusRef = useRef<"content" | "button" | null>(null);

  const handleToggle = () => {
    setIsExpanded((prev) => {
      pendingFocusRef.current = !prev ? "content" : "button";
      return !prev;
    });
  };

  useEffect(() => {
    if (pendingFocusRef.current === "content" && contentRef.current) {
      contentRef.current.focus();
      pendingFocusRef.current = null;
    } else if (pendingFocusRef.current === "button" && toggleRef.current) {
      toggleRef.current.focus();
      pendingFocusRef.current = null;
    }
  }, [isExpanded]);

  // Error message mapping
  const getErrorMessage = (err: unknown): string => {
    if (err instanceof Error) {
      if (err.name === "TimeoutError" || err.message.includes("timeout")) {
        return "Przekroczono czas oczekiwania";
      }
    }
    return "Nie udało się pobrać rekomendacji";
  };

  // Hide section if no other recommendations (after loading or from preloaded count)
  const shouldHide = !isExpanded && (
    (data && recommendations.length === 0) ||
    (initialCount !== undefined && initialCount === 0)
  );
  if (shouldHide) {
    return null;
  }

  // Display count logic:
  // 1. When expanded and loading: show "..."
  // 2. When expanded and loaded: show actual count from API
  // 3. When collapsed: show preloaded initialCount if available, otherwise 0
  const displayCount = isExpanded && isLoading
    ? "..."
    : isExpanded
      ? Math.max(0, totalCount)
      : (initialCount ?? 0);

  return (
    <section
      aria-labelledby="related-recommendations-heading"
      className="bg-white rounded-lg shadow-sm border border-neutral-200"
    >
      <div className="px-6 py-4">
        <button
          ref={toggleRef}
          onClick={handleToggle}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              handleToggle();
            }
          }}
          className="flex items-center justify-between w-full text-left"
          aria-expanded={isExpanded}
          aria-controls="related-recommendations-content"
          id="related-recommendations-toggle"
        >
          <h2 id="related-recommendations-heading" className="text-lg font-medium text-neutral-900">
            Powiązane rekomendacje ({displayCount})
          </h2>
          <ChevronIcon expanded={isExpanded} />
        </button>

        <div
          ref={contentRef}
          id="related-recommendations-content"
          aria-labelledby="related-recommendations-toggle"
          tabIndex={-1}
          className={`mt-4 pt-4 border-t border-neutral-200 ${isExpanded ? "" : "hidden"}`}
        >
          {isLoading && <RecommendationsSkeleton />}

          {error && !isLoading && (
            <ErrorAlert message={getErrorMessage(error)} onRetry={() => refetch()} />
          )}

          {!isLoading && !error && recommendations.length === 0 && (
            <p className="text-center text-sm text-neutral-500 py-2">
              Brak innych rekomendacji w tym projekcie
            </p>
          )}

          {!isLoading && !error && recommendations.length > 0 && (
            <>
              <div className="space-y-2">
                {recommendations.map((rec) => (
                  <RecommendationRow key={rec.id} recommendation={rec} />
                ))}
              </div>
              {totalCount > 20 && (
                <p className="mt-2 text-xs text-neutral-500 text-center">
                  Pokazano 20 najnowszych rekomendacji z {totalCount}
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default RelatedRecommendationsSection;
